if (!customElements.get('reviews-section')) {
  class ReviewsSection extends HTMLElement {
    constructor() {
      super();

      this.slides = Array.from(this.querySelectorAll('.reviews__slide'));
      this.slidesContainer = this.querySelector('.reviews__slides');
      this.dots = Array.from(this.querySelectorAll('.reviews__dot'));
      this.previousButton = this.querySelector('.reviews__arrow--previous');
      this.nextButton = this.querySelector('.reviews__arrow--next');
      this.currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-selected'));
      this.currentIndex = this.currentIndex >= 0 ? this.currentIndex : 0;
      this.autoplay =
        this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplaySpeed = Number(this.dataset.autoplaySpeed) || 5000;
      this.autoplayTimer = null;
      this.touchStartX = null;
      this.touchStartY = null;
      this.touchThreshold = 50;

      this.boundPrevious = this.previous.bind(this);
      this.boundNext = this.next.bind(this);
      this.boundStopAutoplay = this.stopAutoplay.bind(this);
      this.boundStartAutoplay = this.startAutoplay.bind(this);
      this.boundBlockSelect = this.handleBlockSelect.bind(this);
      this.boundTouchStart = this.handleTouchStart.bind(this);
      this.boundTouchEnd = this.handleTouchEnd.bind(this);
      this.boundTouchCancel = this.handleTouchCancel.bind(this);
    }

    connectedCallback() {
      if (this.slides.length === 0) return;

      if (this.previousButton) {
        this.previousButton.addEventListener('click', this.boundPrevious);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.boundNext);
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      this.addEventListener('mouseenter', this.boundStopAutoplay);
      this.addEventListener('mouseleave', this.boundStartAutoplay);
      this.addEventListener('shopify:block:select', this.boundBlockSelect);

      if (this.slidesContainer) {
        this.slidesContainer.addEventListener('touchstart', this.boundTouchStart, { passive: true });
        this.slidesContainer.addEventListener('touchend', this.boundTouchEnd, { passive: true });
        this.slidesContainer.addEventListener('touchcancel', this.boundTouchCancel, { passive: true });
      }

      this.goTo(this.currentIndex, false);
      this.startAutoplay();
    }

    disconnectedCallback() {
      if (this.previousButton) {
        this.previousButton.removeEventListener('click', this.boundPrevious);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.boundNext);
      }

      this.removeEventListener('mouseenter', this.boundStopAutoplay);
      this.removeEventListener('mouseleave', this.boundStartAutoplay);
      this.removeEventListener('shopify:block:select', this.boundBlockSelect);

      if (this.slidesContainer) {
        this.slidesContainer.removeEventListener('touchstart', this.boundTouchStart);
        this.slidesContainer.removeEventListener('touchend', this.boundTouchEnd);
        this.slidesContainer.removeEventListener('touchcancel', this.boundTouchCancel);
      }

      this.stopAutoplay();
    }

    previous() {
      this.goTo(this.currentIndex - 1);
    }

    next() {
      this.goTo(this.currentIndex + 1);
    }

    goTo(index, restartAutoplay = true) {
      if (this.slides.length === 0) return;

      const nextIndex = (index + this.slides.length) % this.slides.length;
      this.currentIndex = nextIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isSelected = slideIndex === nextIndex;
        slide.classList.toggle('is-selected', isSelected);
        slide.setAttribute('aria-hidden', isSelected ? 'false' : 'true');
        slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          if (isSelected) {
            if (element.hasAttribute('data-original-tabindex')) {
              element.setAttribute('tabindex', element.getAttribute('data-original-tabindex'));
              element.removeAttribute('data-original-tabindex');
            } else {
              element.removeAttribute('tabindex');
            }
            if (['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName)) {
              element.removeAttribute('disabled');
            }
            return;
          }

          if (element.hasAttribute('tabindex') && !element.hasAttribute('data-original-tabindex')) {
            element.setAttribute('data-original-tabindex', element.getAttribute('tabindex'));
          }
          element.setAttribute('tabindex', '-1');
          if (['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName)) {
            element.setAttribute('disabled', 'disabled');
          }
        });
      });

      this.dots.forEach((dot, dotIndex) => {
        const isSelected = dotIndex === nextIndex;
        dot.classList.toggle('is-active', isSelected);
        dot.setAttribute('aria-current', isSelected ? 'true' : 'false');
      });

      if (restartAutoplay) {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      this.stopAutoplay();

      if (!this.autoplay || this.slides.length < 2) return;

      this.autoplayTimer = window.setInterval(() => {
        this.next();
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;

      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    handleBlockSelect(event) {
      const selectedSlide = event.target.closest('.reviews__slide');
      if (!selectedSlide) return;

      const index = Number(selectedSlide.dataset.reviewIndex);
      if (Number.isNaN(index)) return;

      this.stopAutoplay();
      this.goTo(index, false);
    }

    handleTouchStart(event) {
      if (event.touches.length !== 1) {
        this.handleTouchCancel();
        return;
      }

      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.stopAutoplay();
    }

    handleTouchEnd(event) {
      if (this.touchStartX === null || this.touchStartY === null || !event.changedTouches.length) return;

      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;
      const deltaX = touchEndX - this.touchStartX;
      const deltaY = touchEndY - this.touchStartY;

      this.handleTouchCancel();

      if (Math.abs(deltaX) < this.touchThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) {
        this.startAutoplay();
        return;
      }

      if (deltaX < 0) {
        this.next();
      } else {
        this.previous();
      }
    }

    handleTouchCancel() {
      this.touchStartX = null;
      this.touchStartY = null;
    }
  }

  customElements.define('reviews-section', ReviewsSection);
}
